import {
	Checkbox,
	DatePicker,
	Form,
	Input,
	Modal,
	Select,
	Space,
	Typography,
} from 'antd';
import { familyHistoryApi } from 'Api/familyHistory';
import TimeLineBaseUI, {
	FamilyHistoryMember,
	FamilyMemberStatus,
} from 'components/Shared/TimeLineBaseUI';
import dayjs from 'dayjs';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toastText } from 'utils/utils';
import styles from './index.module.scss';

const { Text } = Typography;

const requiredMarkAfterLabel = (
	labelNode: ReactNode,
	{ required }: { required: boolean }
) => (
	<>
		{labelNode}
		{required && <span className={styles.requiredMark}>*</span>}
	</>
);

const relationOptions = [
	{ label: 'Spouse', value: 'Spouse' },
	{ label: 'Spouse (Divorced)', value: 'SpouseDivorced' },
	{ label: 'Child', value: 'Child' },
	{ label: 'Parent', value: 'Parent' },
	{ label: 'GrandParent', value: 'GrandParent' },
	{ label: 'Sibling', value: 'Sibling' },
];

type Props = {
	isEditMode: boolean;
};

const FamilyHistory = ({ isEditMode }: Props) => {
	const [searchParams] = useSearchParams();
	const employeeId = searchParams.get('employeeId');
	const [memberModalOpen, setMemberModalOpen] = useState(false);
	const [statusModalOpen, setStatusModalOpen] = useState(false);
	const [reloadKey, setReloadKey] = useState(0);
	const [selectedMember, setSelectedMember] =
		useState<FamilyHistoryMember | null>(null);
	const [memberForm] = Form.useForm();
	const [statusForm] = Form.useForm();
	const familySide = Form.useWatch('family_side', memberForm) ?? 'none';
	const relationWatch = Form.useWatch('relation', memberForm);
	const nextOfKinDisabled = relationWatch === 'SpouseDivorced';

	const resetMemberModalForm = useCallback(() => {
		memberForm.resetFields();
	}, [memberForm]);

	useEffect(() => {
		if (memberModalOpen) {
			memberForm.resetFields();
		}
	}, [memberModalOpen, memberForm]);

	useEffect(() => {
		if (relationWatch === 'SpouseDivorced') {
			memberForm.setFieldsValue({
				is_next_of_kin: false,
				mobile: undefined,
				email: undefined,
			});
		}
	}, [relationWatch, memberForm]);

	if (!employeeId) {
		return (
			<div
				style={{
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					height: '50vh',
				}}
			>
				<Text>
					Add <span className="color-purple">Employment</span> details first to
					manage Family History.
				</Text>
			</div>
		);
	}

	const fetchMembers = async (params: {
		search?: string;
		status?: 'Alive' | 'Deceased' | 'all';
		sort?: 'most_recent';
		limit: number;
		offset: number;
	}) => {
		const response = await familyHistoryApi.getFamilyMembers(employeeId, params);
		return {
			data: response?.data?.data || [],
			total: response?.data?.total || 0,
		};
	};

	const onCreateMember = async () => {
		try {
			const values = await memberForm.validateFields();
			const isKin =
				values.relation !== 'SpouseDivorced' &&
				values.is_next_of_kin === true;
			await familyHistoryApi.addFamilyMember(employeeId, {
				name: values.name.trim(),
				relation: values.relation,
				family_side: values.family_side || 'none',
				is_next_of_kin: isKin,
				mobile:
					isKin && values.mobile ? values.mobile.trim() : null,
				email:
					isKin && values.email
						? values.email.trim().toLowerCase()
						: null,
				date_of_birth: dayjs(values.date_of_birth).format('YYYY-MM-DD'),
			});
			toastText('Family member added successfully', 'success');
			resetMemberModalForm();
			setMemberModalOpen(false);
			setReloadKey((prev) => prev + 1);
		} catch (error: any) {
			if (!error?.errorFields) {
				toastText(
					error?.response?.data?.message || 'Failed to add family member',
					'error'
				);
			}
		}
	};

	const onStatusChange = async () => {
		try {
			if (!selectedMember) return;
			const values = await statusForm.validateFields();
			await familyHistoryApi.changeFamilyMemberStatus(employeeId, selectedMember.id, {
				new_status: values.new_status as FamilyMemberStatus,
				effective_date: dayjs(values.effective_date).format('YYYY-MM-DD'),
				notes: values.notes.trim(),
			});
			toastText('Family member status changed', 'success');
			setStatusModalOpen(false);
			statusForm.resetFields();
			setReloadKey((prev) => prev + 1);
		} catch (error: any) {
			if (!error?.errorFields) {
				toastText(
					error?.response?.data?.message || 'Failed to change status',
					'error'
				);
			}
		}
	};

	return (
		<>
			<Space direction="vertical" style={{ width: '100%' }} size={12}>
				{isEditMode && (
					<Text type="secondary">
						Edit mode: You can add new members and change status. Existing core
						member details are not editable.
					</Text>
				)}
				<TimeLineBaseUI
					readOnly={false}
					reloadKey={reloadKey}
					fetchMembers={fetchMembers}
					onAddMember={() => setMemberModalOpen(true)}
					onChangeStatus={(member) => {
						setSelectedMember(member);
						statusForm.setFieldsValue({
							new_status:
								member.current_status === 'Alive' ? 'Deceased' : 'Alive',
							effective_date: dayjs(),
							notes: '',
						});
						setStatusModalOpen(true);
					}}
				/>
			</Space>

			<Modal
				open={memberModalOpen}
				onCancel={() => {
					resetMemberModalForm();
					setMemberModalOpen(false);
				}}
				afterClose={resetMemberModalForm}
				destroyOnClose
				onOk={onCreateMember}
				width="600px"
				closable={false}
				maskClosable={false}
				okText="Add Member"
				title="Add Family Member"
			>
				<Form
					className={styles.familyHistoryForm}
					form={memberForm}
					layout="vertical"
					requiredMark={requiredMarkAfterLabel}
					initialValues={{
						family_side: 'none',
						is_next_of_kin: false,
						name: undefined,
						relation: undefined,
						date_of_birth: undefined,
						mobile: undefined,
						email: undefined,
					}}
				>
					<Form.Item
						name="name"
						label="Name"
						rules={[{ required: true, message: 'Name is required' }]}
					>
						<Input placeholder="Enter full name" />
					</Form.Item>
					<Form.Item
						name="relation"
						label="Relation"
						rules={[{ required: true, message: 'Relation is required' }]}
					>
						<Select
							options={relationOptions}
							placeholder="Select relation"
							onChange={(value) => {
								if (value === 'SpouseDivorced') {
									memberForm.setFieldsValue({
										is_next_of_kin: false,
										mobile: undefined,
										email: undefined,
									});
								}
							}}
						/>
					</Form.Item>
					<Form.Item name="family_side" hidden>
						<Input />
					</Form.Item>
					<Form.Item label="Relation Side">
						<Space>
							<Checkbox
								checked={familySide === 'maternal'}
								onChange={(event) => {
									memberForm.setFieldsValue({
										family_side: event.target.checked ? 'maternal' : 'none',
									});
								}}
							>
								Maternal
							</Checkbox>
							<Checkbox
								checked={familySide === 'paternal'}
								onChange={(event) => {
									memberForm.setFieldsValue({
										family_side: event.target.checked ? 'paternal' : 'none',
									});
								}}
							>
								Paternal
							</Checkbox>
						</Space>
					</Form.Item>
					<Form.Item name="is_next_of_kin" valuePropName="checked">
						<Checkbox
							disabled={nextOfKinDisabled}
							onChange={(event) => {
								if (!event.target.checked) {
									memberForm.setFieldValue('mobile', undefined);
									memberForm.setFieldValue('email', undefined);
								}
							}}
						>
							Next of Kin
						</Checkbox>
					</Form.Item>
					<Form.Item noStyle shouldUpdate>
						{({ getFieldValue }) =>
							getFieldValue('is_next_of_kin') &&
							getFieldValue('relation') !== 'SpouseDivorced' ? (
								<>
									<Form.Item
										name="mobile"
										label={
											<span className={styles.fieldLabel}>
												Mobile Number
												<span className={styles.requiredMark}>*</span>
											</span>
										}
										dependencies={[
											'is_next_of_kin',
											'email',
											'relation',
										]}
										rules={[
											({ getFieldValue: get }) => ({
												validator(_, value) {
													if (
														!get('is_next_of_kin') ||
														get('relation') === 'SpouseDivorced'
													) {
														return Promise.resolve();
													}
													const trimmed = String(value ?? '').trim();
													if (!trimmed) {
														return Promise.reject(
															new Error('Mobile number is required'),
														);
													}
													if (!/^[0-9]{10}$/.test(trimmed)) {
														return Promise.reject(
															new Error(
																'Mobile number must be exactly 10 digits',
															),
														);
													}
													return Promise.resolve();
												},
											}),
										]}
									>
										<Input
											maxLength={10}
											inputMode="numeric"
											placeholder="Enter 10-digit mobile number"
										/>
									</Form.Item>
									<Form.Item
										name="email"
										label="Email"
										dependencies={[
											'is_next_of_kin',
											'mobile',
											'relation',
										]}
										rules={[
											({ getFieldValue: get }) => ({
												validator(_, value) {
													if (
														!get('is_next_of_kin') ||
														get('relation') === 'SpouseDivorced'
													) {
														return Promise.resolve();
													}
													const trimmed = String(value ?? '').trim();
													if (!trimmed) {
														return Promise.resolve();
													}
													const emailOk =
														/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
													if (!emailOk) {
														return Promise.reject(
															new Error('Enter a valid email address'),
														);
													}
													return Promise.resolve();
												},
											}),
										]}
									>
										<Input placeholder="Enter email address" />
									</Form.Item>
								</>
							) : null
						}
					</Form.Item>
					<Form.Item
						name="date_of_birth"
						label="Date of Birth"
						rules={[{ required: true, message: 'Date of birth is required' }]}
					>
						<DatePicker style={{ width: '100%' }} />
					</Form.Item>
				</Form>
			</Modal>

			<Modal
				open={statusModalOpen}
				onCancel={() => setStatusModalOpen(false)}
				onOk={onStatusChange}
				okText="Change Status"
				title={`Change Status - ${selectedMember?.name || ''}`}
			>
				<Form
					className={styles.familyHistoryForm}
					form={statusForm}
					layout="vertical"
					requiredMark={requiredMarkAfterLabel}
				>
					<Form.Item
						name="new_status"
						label="New Status"
						rules={[{ required: true, message: 'Status is required' }]}
					>
						<Select
							options={[
								{ label: 'Alive', value: 'Alive' },
								{ label: 'Deceased', value: 'Deceased' },
							]}
						/>
					</Form.Item>
					<Form.Item
						name="effective_date"
						label="Effective Date"
						rules={[{ required: true, message: 'Effective date is required' }]}
					>
						<DatePicker style={{ width: '100%' }} />
					</Form.Item>
					<Form.Item
						name="notes"
						label="Notes"
						rules={[{ required: true, message: 'Notes are required' }]}
					>
						<Input.TextArea rows={4} />
					</Form.Item>
				</Form>
			</Modal>
		</>
	);
};

export default FamilyHistory;
